// searchVocab.js — the WORDS the Desk search box understands. DATA ONLY, no logic:
//
// ⚠️ VERSION RULE: bump VOCAB_VERSION by 1 EVERY time you change anything in this file.
// The version is stamped on every search_query_log row, so old logs keep their meaning
// ("ნუცუბიძე meant Saburtalo under v3, its own district under v9") and improvements
// become measurable ("how many v3 residuals would v9 compile?") instead of anecdotal.
// The immutable snapshot of each version is this file's git history — never edit
// without committing.
export const VOCAB_VERSION = 32   // v32 (2026-11-04, owner "close gaps"): appliance words in their case forms — кондиционером, холодильником, стиральной машиной, посудомоечной машиной, მაცივარი, სარეცხი მანქანა — and TV (ტელევიზორი, телевизор, tv); "квартира с кондиционером" searched nothing. v31 (2026-11-03, owner: Desk pets/smoking check): smoking words for pol_smoker (smoking, smokers, მოწევა, курить, курящие, курение) — "smoking allowed" searched nothing. v30 (2026-10-28, public search audit): the site's own property-type labels in German, Japanese, Chinese, Turkish and Arabic (Grundstück, 土地, Arsa, أرض …) — they stayed text and the previous type was kept. v29 (2026-10-22, typed-search audit): Georgian area units (მ², მ2, კვ. მ, მეტრი, კვადრატული მეტრი), "-ზე მეტი"/"-ზე ნაკლები" price directions, negated floor phrases (არაპირველი, ბოლო სართულზე არა, არ მინდა პირველი), district negation words (არა/not/except/кроме/გარდა), გარდა never a place, count minimum words (მინიმუმ 3 ოთახი), floor above/below words (3 სართულზე მაღლა), per-area price markers, and leftover modifier words as stopwords (სართული, თვეში, cheap/იაფი, between). v28 (2026-10-02, owner): apart-hotel words (CONTEXT_WORDS) — apartment + type_aparthotel_unit, claimed before the commercial `hotel` subtype word. v27 (2026-09-30, reviewer finding): the budget word is read wherever it stands — after the amount ("200000 budget"), with a small word before the amount ("budget of 150000", "ბიუჯეტი არის 150000"), and in Russian ("бюджет"); PRICE_BUDGET_GLUE. v26 (2026-09-30, reviewer finding): the words that say a feature is ABSENT (FEATURE_NEGATION_*) — "ლიფტის გარეშე" / "without a lift" / "ლიფტი არ აქვს" compiled to lift = YES. v25 (2026-09-29, owner test 2): Latin/slash area units (kv.m, კვ/მ), unit words carrying a bound (კვადრატამდე), სასაწყობე, tbilishi, "project" as a stopword, Soviet-series everyday names (ხრუშჩოვკა, chexuri, kavlashvilis). v24 (2026-09-29, public search test of 213 queries): plural/case forms of kinds and subtypes (apartments, квартиру, ნაკვეთის, აგარაკის, дачу), Russian accusative room words (однушку, двухкомнатную), Georgian/Russian studio cases, Latin-typed lari/dollar words, English/Russian glue words and other sites' names as stopwords, spaced portal names, Latin cases of Tbilisi. v23 (2026-09-25): numbered sub-area type words (AREA_TYPE_WORDS). v22 (2026-09-25): Tbilisi in ar/ja/zh + Tiflis (Tbilisi has no district row; every other place's translations come from district_name_i18n). v21   // v21 (2026-09-25, public search audit): floor negation + ranges, area bounds, Russian price/currency/deal words, 'near metro' phrases — the public site now compiles through this file, so its misses are ours
// v20 was:   // v20: international room conventions (ROOM_CONVENTIONS) — BHK/bed/BR/dormitorios/LDK → bedrooms, Zimmer/pièces/T3/комнаты/bilocale → rooms, bath/baño → bathrooms, studio → layout_studio (owner 2026-09-24)
// v19 was:   // v19: F-101 road-type words never resolve a district alone; English ordinals (2026-09-10)
// v18 was:   // v18: B302 — the three payment words (გადარიცხვა/კრიპტო/განვადება) removed; their 0400-deactivated codes shelved the whole corpus, and an unclaimed fragment at least logs;  v17: daily_rent became typeable — the fourth live intent, previously reachable only by clicking the Deal facet;  v16: property SUBTYPES (კოტეჯი/ოფისი/საწყობი/სასტუმრო/სასოფლო…) became searchable, and stopped being flattened into their parent kind
//
// the matching engine lives in searchCompiler.js and never needs to change when you
// add words here. This file is meant to be edited by hand as real queries come in —
// check the search_query_log table (core DB, migration 0049) to see what people
// actually typed, what compiled, and what they typed that did NOT compile (residual);
// add the missing words/typos here.
//
// Rules of thumb when adding:
//  - lowercase everything; the engine lowercases the query before matching.
//  - Georgian NOUNS (districts, "ოთახი") are matched through the stemmer, so you only
//    need the base form — ვაკე also matches ვაკეში/ვაკეზე automatically. Georgian VERB
//    forms (იყიდება/ვყიდი/ქირავდება) do NOT stem well — list each form explicitly.
//  - typos are first-class vocabulary: if the log shows people type "vake" as "wake",
//    add "wake". That's how this gets smarter — no ML, just observed usage.

// ── deal type (→ panel "Deal") ────────────────────────────────────────────────
// v4: latin-transliteration + russian coverage from the Gemini keyword pass
// (core/data/gemini-vocab/property-keywords.merged.json) — before v4 a latin-typing
// agent ("iyideba bina vake") compiled to NOTHING on deal/kind/room concepts.
// Georgian verb forms don't stem — every latin verb form is listed explicitly too.
export const DEAL_WORDS = {
  // v17: daily_rent was a LIVE intent (0219-era; 15 active listings), offered by the Deal
  // facet and by manual creation, and NOT typeable — the same one-surface-short gap subtype
  // had. ⚠️ IT MUST STAY FIRST IN THIS OBJECT. `recognizeWordMap` iterates Object.entries and
  // spans are first-claim-wins, so if `rent` ran first its bare 'ქირა' would claim the tail of
  // "დღიური ქირა" and the query would compile as ordinary rent PLUS daily — two intents from
  // one phrase. Running daily_rent first lets its longer phrase claim the whole span (alt()
  // sorts longest-first within a value), after which rent's 'ქირა' overlaps and is skipped.
  daily_rent: ['დღიური ქირა', 'დღიურად ქირავდება', 'დღით ქირავდება', 'დღიური', 'დღიურად',
               'daily rent', 'daily rental', 'short term', 'short-term', 'per day', 'daily',
               'dgiuri qira', 'dgiuri', 'dgiurad',
               'посуточная аренда', 'посуточно', 'посуточная'],
  // v10: word-form gaps from the 2026-07-18 hunt (გასაყიდი/იჯარით etc → no deal chip).
  sale: ['buy', 'buying', 'sale', 'purchase', 'იყიდება', 'ვყიდი', 'ყიდვა', 'ყიდვით', 'ვიყიდი', 'ვიყიდით', 'საყიდლად',
         'გასაყიდი', 'გასაყიდად', 'გავყიდი',
         'iyideba', 'ikideba', 'iqideba', 'gayidva', 'gasayidi', 'продам', 'продается', 'продаётся',
         'купить', 'куплю', 'продажа', 'kaufen', 'satılık',
         'sell', 'selling', 'ვყიდულობ', 'покупка', 'продажи', 'продаю', 'kupit'],   // v21, v24
  rent: ['rent', 'rental', 'renting', 'lease', 'ქირავდება', 'ვაქირავებ', 'ქირით', 'დაქირავება', 'ვიქირავებ', 'გასაქირავებლად', 'ქირა',
         'იჯარა', 'იჯარით', 'საიჯარო', 'გასაქირავებელი', 'ვქირაობ',
         'qiravdeba', 'kiravdeba', 'gaqiraveba', 'gakiraveba', 'gasaqiravebeli', 'qira', 'сдается', 'сдаётся', 'сдам', 'аренда',
         'в аренду', 'аренду', 'снять', 'сниму', 'miete', 'mieten', 'kiralık', 'kira',
         'rentals', 'rents', 'арендовать', 'аренды', 'сдаю', 'снимать', 'snat', 'arenda'],   // v21 (L-ru3), v24
  // გირავდება (girao) — LIVE as of 0219 (2026-07-27). The precondition written here on
  // 2026-07-06 ("a mapping would compile a chip the panel can't represent") is met: the
  // Deal panel reads LISTING_INTENT_OPTIONS, which now carries girao, and BROWSE_POLICY's
  // deal rule is generic (`{intents:[value]}`), so no policy change was needed either.
  girao: ['გირავდება', 'giravdeba', 'გირაო', 'girao', 'გირავნობა', 'იგირავება', 'залог', 'в залог'],
  // ⚠️ NO `ijara` entry, and `იჯარა`/`იჯარით`/`საიჯარო` stay under RENT above — on
  // purpose. myhome has a separate იჯარა deal type (10) which now gets its own intent
  // label, but in speech Georgian agents use იჯარა loosely for ordinary renting, so
  // compiling those words to an ijara-only filter would hide the rentals they actually
  // meant. Label the SUPPLY precisely; keep the QUERY forgiving.
}

// ── property type (→ panel "Type"; values must match PROPERTY_KIND_OPTIONS) ──
// v4 note: single-word russian 'дом'/'квартира' are safe (engine matches whole words),
// but ultra-short forms from the Gemini pass ('кв', 'ac') are deliberately EXCLUDED —
// same substring-poison class as the alias collision rule below.
// v16: the words that name a SUBTYPE moved OUT of here and into SUBTYPE_WORDS below —
// კოტეჯი/ვილა/ოფისი/საწყობი/მაღაზია used to compile to their parent KIND, which flattened
// the grammar's Identity hierarchy at query time exactly the way ingest used to flatten it
// at capture time (B252). They still narrow to the same kind: the subtype policy emits BOTH
// `kinds` and `subtypes`, so nothing an agent could find before became unfindable.
export const KIND_WORDS = {
  apartment:  ['apartment', 'apt', 'flat', 'ბინა', 'ბინის', 'ბინას', 'ბინები', 'ბინაში', 'აპარტამენტი',
               'bina', 'binis', 'kvartira', 'apartamenti', 'квартира', 'апартаменты',
               // v24 (2026-09-29 public test): plurals and cases people type — "apartments for rent" and "снять квартиру"
               // had no type at all
               'apartments', 'apts', 'flats', 'condo', 'condos', 'ბინებს', 'ბინების', 'ბინებში',
               'квартиру', 'квартиры', 'квартир', 'квартире', 'квартирой', 'апартамент', 'апартаментов', 'kvartiru', 'kvartiry',
               // v30 (2026-10-28, public search audit): the site's own type label in de/ja/zh/tr/ar — typing the word the
               // site itself shows left it as text and kept the previous type. ⚠️ Keep in step with market.ui.<kind> there.
               'wohnung', 'wohnungen', 'マンション', '公寓', 'daire', 'شقة'],
  house:      ['house', 'სახლი', 'სახლის', 'სახლს', 'სახლები', 'დაჩა',
               'saxli', 'sakhli', 'kerdzo', 'дом',
               'houses', 'კერძო', 'სახლებს', 'სახლების', 'дома', 'домов', 'домик', 'частный дом',   // v24
               'haus', 'häuser', '一戸建て', '独栋住宅', 'müstakil ev', 'منزل'],   // v30 — see apartment
  land:       ['land', 'plot', 'lot', 'მიწა', 'მიწის', 'ნაკვეთი',
               'mitsa', 'miwa', 'nakveti', 'nakvet', 'участок', 'земля',
               // v24 — NOT 'участком': "дом с участком" is a house WITH land, not a plot
               'lands', 'plots', 'ნაკვეთის', 'ნაკვეთები', 'ნაკვეთს', 'участка', 'участки', 'земельный', 'землю',
               'grundstück', 'grundstücke', '土地', 'arsa', 'أرض'],   // v30 — see apartment
  commercial: ['commercial', 'კომერციული', 'ფართი', 'komerciuli', 'farti',
               'ფართის', 'ფართები', 'коммерческая', 'коммерческую', 'коммерческой', 'коммерческое', 'коммерческая недвижимость',
               'коммерческую недвижимость', 'помещение', 'помещения', 'помещений',   // v24
               'gewerbe', '商業用', '商业', 'ticari', 'تجاري'],   // v30 — see apartment
}

// ── property SUBTYPE (→ panel "Type" chips under a kind; grammar §5 Identity layer 3) ──
// v16. Values MUST be the canonical tokens of `subtype_map.js` / the server's `subtypes`
// whitelist / SUBTYPES_BY_KIND in the Desk. The chain for a new token is:
//   SEARCH_GRAMMAR.md §5  →  subtype_map.js  →  search_filters.js whitelist  →  Desk  →  here.
// A token here that the whitelist does not carry makes the SERVER REFUSE the request
// (failClosed: 'error'), which is loud — but it is still a bug, so keep the five in step.
//
// ⚠️ DELIBERATELY ABSENT: `garage` and `parking_space`. The canonical tokens exist and the
// facet offers them, but the WORDS are poison in a search box: measured 2026-08-01, all 402
// "ავტოფარეხი" text hits across the corpus are flats WITH a garage, not garages for sale —
// that is the `garage_*` FEATURE codes' job (engine v19), and those recognizers run later.
// Claiming the span here would turn "ავტოფარეხით ბინა" (flat with garage) into a search for
// garages. Same substring-poison class as the 'кв'/'ac' exclusion noted above.
// ── apartment CONTEXT (v28, owner 2026-10-02) ──────────────────────────────────
// An apart-hotel unit is an APARTMENT run as part of a hotel operation — kind=apartment plus
// the feature `type_aparthotel_unit` (core 1940). It is NOT the commercial `hotel` subtype
// below, which is a whole hotel business. These words must be claimed BEFORE SUBTYPE_WORDS:
// "apart hotel" used to fall through to `hotel` and searched for commercial hotels.
// Keys are taxonomy.js APARTMENT_CONTEXT nodes. The Georgian/Latin forms are the registry's own
// aliases (1940) plus their case endings.
export const CONTEXT_WORDS = {
  aparthotel: ['აპარტოტელი', 'აპარტოტელის', 'აპარტოტელში', 'აპარტოტელები', 'აპარტოტელებში',
               'აპარტ-ოტელი', 'აპარტ ოტელი', 'აპარტჰოტელი', 'aparthotel', 'aparthotels', 'apart-hotel',
               'apart hotel', 'apart-hotels', 'apart hotels', 'apartotel', 'aparthotelshi',
               'апарт-отель', 'апарт отель', 'апартотель', 'апарт-отеле', 'апарт-отеля', 'апарт-отели'],
}

export const SUBTYPE_WORDS = {
  // house
  cottage:        ['cottage', 'კოტეჯი', 'koteji', 'коттедж', 'cottages', 'cabin', 'cabins', 'კოტეჯის', 'კოტეჯები', 'коттеджа', 'коттеджи', 'коттеджей'],
  country_house:  ['აგარაკი', 'agaraki', 'дача', 'აგარაკის', 'აგარაკები', 'აგარაკს', 'дачу', 'дачи', 'дач', 'дачный'],
  townhouse:      ['townhouse', 'თაუნჰაუსი', 'taunhausi', 'таунхаус', 'townhouses', 'townhome', 'townhomes', 'таунхауса', 'таунхаусы'],
  villa:          ['villa', 'ვილა', 'vila', 'вилла', 'villas', 'ვილები', 'виллу', 'виллы'],
  // commercial
  office:         ['office', 'ოფისი', 'ofisi', 'opisi', 'saofise', 'საოფისე', 'офис', 'offices', 'ოფისის', 'ოფისები', 'офиса', 'офисы', 'офисов', 'офисное'],
  retail:         ['shop', 'retail', 'მაღაზია', 'magazia', 'სავაჭრო', 'savachro', 'магазин', 'shops', 'магазина', 'магазины'],
  warehouse:      ['warehouse', 'საწყობი', 'sawyobi', 'satsqobi', 'склад', 'სასაწყობე', 'sasatsqobe', 'sasawyobe', 'warehouses', 'საწყობის', 'склада', 'склады', 'складов'],
  hotel:          ['hotel', 'hostel', 'სასტუმრო', 'sastumro', 'ჰოსტელი', 'отель', 'гостиница'],
  restaurant_cafe: ['restaurant', 'cafe', 'რესტორანი', 'კაფე', 'restorani', 'kafe', 'ресторан', 'кафе'],
  production_space: ['საწარმოო', 'sawarmoo', 'satsarmoo', 'производственное'],
  universal_space:  ['უნივერსალური', 'universaluri'],
  non_residential_space: ['არასაცხოვრებელი', 'arasacxovrebeli'],
  special_purpose:  ['სპეციალური დანიშნულების', 'specialuri danishnulebis'],
  // land
  agricultural:     ['სასოფლო', 'სასოფლო-სამეურნეო', 'sasoflo', 'сельскохозяйственный'],
  non_agricultural: ['არასასოფლო', 'არასასოფლო-სამეურნეო', 'arasasoflo', 'несельскохозяйственный'],
}

// Which kind each subtype belongs to — the policy emits BOTH, so a typed subtype narrows
// the kind too and can never return less than the bare kind word used to.
export const SUBTYPE_PARENT_KIND = {
  cottage: 'house', country_house: 'house', townhouse: 'house', villa: 'house',
  office: 'commercial', retail: 'commercial', warehouse: 'commercial', hotel: 'commercial',
  restaurant_cafe: 'commercial', production_space: 'commercial', universal_space: 'commercial',
  non_residential_space: 'commercial', special_purpose: 'commercial',
  garage: 'commercial', parking_space: 'commercial',
  agricultural: 'land', non_agricultural: 'land',
}

// ── words that make an adjacent number mean rooms/bedrooms/floor ─────────────
// (nouns — matched via the stemmer, base form is enough; latin forms don't go
// through the Georgian stemmer, so suffixed variants are listed explicitly)
//
// ── v20: HOW PEOPLE FROM OTHER COUNTRIES COUNT ROOMS (owner 2026-09-24) ──────
// One flat, seven naming systems. They are SEARCH-LANGUAGE ALIASES onto the facts we
// already keep — never new fields per country. The distinction that matters most:
//   BEDROOM-count systems:  2 BHK · 2 bed · 2 BR · 2 dormitorios · 2LDK · 2+1
//   TOTAL-ROOM systems:     3 Zimmer · 3 комнаты · 3 ოთახიანი · T3 · 3 pièces · trilocale
// "3 ოთახიანი" is usually 2 bedrooms + a living room, so a total-room word must NEVER
// compile to bedrooms. Each row names its concept; the engine reads the table, so a new
// convention is a row here, not a regex there. `form`: 'count' = a number before the word
// ("2 bhk", "2bhk"); 'prefix' = a letter before the number ("T3"); 'value' = the word IS
// the count ("bilocale" = 2); 'flag' = the word is the fact itself ("studio").
// `interpretation` records how sure the convention is — 'likely' rows are still applied,
// the column says why a reviewer may want to revisit them.
export const ROOM_CONVENTIONS = [
  // bedrooms — South Asia, North America, UK/Ireland, Gulf, Spain/LatAm, France, Germany, Japan
  { concept: 'bedrooms', form: 'count', locale: 'en-IN', interpretation: 'exact', expr: ['bhk', 'bk'] },
  { concept: 'bedrooms', form: 'count', locale: 'en', interpretation: 'exact', expr: ['beds', 'bd', 'bds', 'br', 'bdr', 'bdrm', 'bdrms', 'bedrm'] },
  { concept: 'bedrooms', form: 'count', locale: 'es', interpretation: 'exact', expr: ['dormitorio', 'dormitorios', 'dorm', 'dorms', 'habitacion', 'habitación', 'habitaciones', 'recamara', 'recámara', 'recamaras', 'recámaras'] },
  { concept: 'bedrooms', form: 'count', locale: 'fr', interpretation: 'exact', expr: ['chambre', 'chambres'] },
  { concept: 'bedrooms', form: 'count', locale: 'de', interpretation: 'exact', expr: ['schlafzimmer'] },
  { concept: 'bedrooms', form: 'count', locale: 'ru', interpretation: 'exact', expr: ['спален', 'спальнями'] },
  // Japan: the number before LDK/DK is the number of OTHER rooms (SUUMO's definition) — bedrooms
  { concept: 'bedrooms', form: 'count', locale: 'ja', interpretation: 'likely', expr: ['ldk', 'sldk', 'dk'] },
  // v21: the site's remaining languages (public audit B-LANG-2) — exact bedroom words, not total rooms
  { concept: 'bedrooms', form: 'count', locale: 'tr', interpretation: 'exact', expr: ['yatak odası', 'yatak odasi', 'yatak odalı', 'yatak odali'] },
  { concept: 'bedrooms', form: 'count', locale: 'ar', interpretation: 'exact', expr: ['غرف نوم', 'غرفة نوم', 'غرفتين نوم', 'غرفتي نوم'] },
  { concept: 'bedrooms', form: 'count', locale: 'ja', interpretation: 'exact', expr: ['寝室', 'ベッドルーム'] },
  { concept: 'bedrooms', form: 'count', locale: 'zh', interpretation: 'exact', expr: ['卧室', '间卧室'] },
  // Arabic states a count of two with the DUAL — no digit to attach a unit to
  { concept: 'bedrooms', form: 'value', locale: 'ar', interpretation: 'exact', expr: { 'غرفتين نوم': 2, 'غرفتي نوم': 2, 'غرفتا نوم': 2, 'غرفة نوم واحدة': 1 } },
  // total rooms — Germany/Austria, Russia/CIS, France, Italy, Korea
  { concept: 'rooms', form: 'count', locale: 'de', interpretation: 'exact', expr: ['zimmer', 'zi.'] },
  { concept: 'rooms', form: 'count', locale: 'ru', interpretation: 'exact', expr: ['комнаты', 'комнат', 'комнатную', 'комнатной', 'комнатный', 'комн.'] },
  // ⚠️ NOT 'к'/'к.': "300к" is a price in thousands, and a count word has no upper bound — it would read as 300 rooms
  { concept: 'rooms', form: 'count', locale: 'fr', interpretation: 'exact', expr: ['pièce', 'pièces', 'piece', 'pieces', 'pcs'] },
  { concept: 'rooms', form: 'prefix', locale: 'fr', interpretation: 'likely', expr: ['t', 'f'] },
  { concept: 'rooms', form: 'value', locale: 'it', interpretation: 'likely', expr: { bilocale: 2, trilocale: 3, quadrilocale: 4, pentalocale: 5 } },
  { concept: 'rooms', form: 'value', locale: 'ko', interpretation: 'likely', expr: { '투룸': 2, '쓰리룸': 3, '포룸': 4 } },
  // bathrooms — North America, Spain/LatAm, Italy, Germany, Russia, Georgia
  { concept: 'bathrooms', form: 'count', locale: 'en', interpretation: 'exact', expr: ['bath', 'baths', 'ba', 'bathroom', 'bathrooms', 'bthrm', 'bthrms'] },
  { concept: 'bathrooms', form: 'count', locale: 'es', interpretation: 'exact', expr: ['baño', 'baños', 'bano', 'banos'] },
  { concept: 'bathrooms', form: 'count', locale: 'it', interpretation: 'exact', expr: ['bagno', 'bagni'] },
  { concept: 'bathrooms', form: 'count', locale: 'de', interpretation: 'exact', expr: ['badezimmer', 'bäder', 'bad'] },
  { concept: 'bathrooms', form: 'count', locale: 'ru', interpretation: 'exact', expr: ['санузел', 'санузла', 'санузлов', 'ванных', 'ванные'] },
  { concept: 'bathrooms', form: 'count', locale: 'ka', interpretation: 'exact', expr: ['სააბაზანო', 'სააბაზანოთი', 'სველი წერტილი', 'სველი წერტილით', 'saabazano'] },
  // studio — the recorded layout fact (feature layout_studio, 2,698 properties 2026-09-24), not a room count:
  // "studio ≠ reliably 1 room" (see ROOM_VALUE_WORDS) still holds, so it compiles to the layout, never to rooms=1
  { concept: 'studio', form: 'flag', locale: 'intl', interpretation: 'exact', expr: ['studio', 'studios', 'სტუდიო', 'სტუდიოს', 'სტუდიო-ტიპის', 'სტუდია', 'სტუდიას', 'სტუდიის', 'студия', 'студию', 'студии', 'гостинка', 'гостинку', 'estudio', 'monolocale', 'einzimmerwohnung', '원룸', '1r'] },
]
const conventionWords = (concept, form) => ROOM_CONVENTIONS.filter(r => r.concept === concept && r.form === form).flatMap(r => (Array.isArray(r.expr) ? r.expr : Object.keys(r.expr)))
const conventionValues = (concept) => Object.assign({}, ...ROOM_CONVENTIONS.filter(r => r.concept === concept && r.form === 'value').map(r => r.expr))

export const ROOM_WORDS    = ['room', 'rooms', 'rm', 'ოთახი', 'ოთახიან', 'ოთახიანი',
                              'otaxi', 'otaxiani', 'otakhiani', 'komnatnaya', 'комнатная', 'комн',
                              ...conventionWords('rooms', 'count')]
export const BEDROOM_WORDS = ['bedroom', 'bedrooms', 'bed', 'საძინებელი', 'საძინებლიანი',
                              'sadzinebeli', 'sajinebeli', 'спальни', 'спальня',
                              ...conventionWords('bedrooms', 'count')]
export const BATHROOM_WORDS = conventionWords('bathrooms', 'count')
export const ROOM_PREFIX_LETTERS = conventionWords('rooms', 'prefix')
export const STUDIO_WORDS = conventionWords('studio', 'flag')
// ── top floor (v11, engine v15) ──────────────────────────────────────────────
// "ბოლო სართული" = the unit's floor EQUALS the building's total floors — not a
// number, a cross-column predicate (floor = floors_total; RPC v11 p_top_floor).
// Multi-word phrases, matched whole.
export const TOP_FLOOR_WORDS = [
  'ბოლო სართული', 'ბოლო სართულზე', 'ბოლო სართულს', 'ზედა სართული', 'ზედა სართულზე',
  'bolo sartuli', 'bolo sartulze', 'last floor', 'top floor', 'последний этаж',
]

// v21: NEGATED floor words — "not first floor" was compiling to floor = 1, the exact opposite
// (public audit B-FLOOR-1). They must run BEFORE every floor recognizer (first claim wins).
// Values are the Desk panel's own ✕ halves (firstFloorNot / topFloorNot) and the public API's
// floor flags (not_first / not_top): one filter, two writers, as everywhere else in this file.
export const FLOOR_FLAG_PHRASES = {
  not_first: ['not first floor', 'not the first floor', 'not on the first floor', 'not ground floor', 'not on the ground floor',
              'no first floor', 'not 1st floor', 'except first floor', 'except the first floor',
              'არა პირველი სართული', 'არა პირველ სართულზე', 'არ იყოს პირველი', 'პირველის გარდა', 'პირველი სართულის გარდა',
              'ara pirveli sartuli', 'не первый этаж',
              // v29 (2026-10-22): the negation after the floor, the fused არა-, and "I don't want" — all read as floor = 1 before
              'არაპირველი სართული', 'არაპირველ სართულზე', 'არაპირველი', 'პირველი სართული არა', 'პირველ სართულზე არა',
              'არ მინდა პირველი სართული', 'არ მინდა პირველ სართულზე', 'პირველი სართული არ მინდა', 'პირველ სართულზე არ მინდა', 'не на первом этаже', 'не первом этаже', 'кроме первого', 'кроме первого этажа'],
  not_top:   ['not top floor', 'not the top floor', 'not last floor', 'not the last floor', 'not on the top floor', 'except top floor',
              'except the top floor', 'არა ბოლო სართული', 'არა ბოლო სართულზე', 'არ იყოს ბოლო', 'ბოლოს გარდა', 'ბოლო სართულის გარდა',
              'ara bolo sartuli', 'не последний этаж',
              // v29 (2026-10-22): "ბოლო სართულზე არა" compiled to topFloor = true, the opposite
              'არაბოლო სართული', 'არაბოლო სართულზე', 'ბოლო სართული არა', 'ბოლო სართულზე არა',
              'არ მინდა ბოლო სართული', 'არ მინდა ბოლო სართულზე', 'ბოლო სართული არ მინდა', 'ბოლო სართულზე არ მინდა', 'не на последнем этаже', 'кроме последнего', 'кроме последнего этажа'],
}

// v21: "near metro" WITHOUT a station — a distance band, not a place. Public mode only: the Desk
// has station filters (metroIds) and no "any station within X" field, and a Desk chip that
// filters nothing is the half-existing-key trap. 700 m is the public API's middle band (300/700/1500).
export const METRO_NEAR_PHRASES = ['near metro', 'near the metro', 'close to metro', 'close to the metro', 'next to metro',
  'metro nearby', 'by the metro', 'walk to metro', 'მეტროსთან ახლოს', 'მეტროსთან', 'მეტროს მახლობლად', 'მეტროს გვერდით',
  'metrostan', 'metrostan axlos', 'metrostan akhlos', 'metros tan', 'metros akhlos', 'metros axlos', 'у метро', 'рядом с метро', 'возле метро', 'около метро', 'недалеко от метро',
  'nahe u-bahn', 'metroya yakın', 'metro yakını']
export const METRO_NEAR_M = 700

// v9: locative inflections added ("მე-5 სართულზე" compiled to nothing); 'სართულიანი'
// REMOVED — "9 სართულიანში" / "ორსართულიანი სახლი" state the BUILDING's storey count,
// not a unit floor, and were compiling to a wrong floor filter (2026-07-18 hunt).
export const FLOOR_WORDS   = ['floor', 'სართული', 'სართულზე', 'სართულს',
                              'sartuli', 'sartulze', 'этаж', 'этаже',
                              'floors', 'სართულები', 'этажи', 'этажа']   // v21: plural forms for ranges ("floors 1-3")

// ── words that ARE a room count (engine ≥4 value-word recognizer) ────────────
// For forms with no separate number the number+unit recognizer could find:
// russian colloquials (двушка) and latin fused number-words (orotaxiani).
// Georgian fused forms (ოროთახიანი) are deliberately ABSENT — NUMBER_WORDS +
// ROOM_WORDS already match them generatively (the regex allows a zero gap);
// listing them here would just create a second competing source of truth.
// 'studia'/'студия' also deliberately absent: studio ≠ reliably 1 room in this
// market (often a kind/layout claim) — stays residual until the data says otherwise.
export const ROOM_VALUE_WORDS = {
  'однушка': 1, 'однокомнатная': 1, 'ertotaxiani': 1, 'erttotaxiani': 1,
  'двушка': 2, 'двухкомнатная': 2, 'orotaxiani': 2,
  'трешка': 3, 'трёшка': 3, 'трехкомнатная': 3, 'трёхкомнатная': 3, 'samotaxiani': 3,
  'четырехкомнатная': 4, 'четырёхкомнатная': 4, 'otxotaxiani': 4,
  'пятикомнатная': 5, 'xutotaxiani': 5,
  // v24: the accusative everyone types after снять/купить ("снять однушку", "купить двухкомнатную")
  'однушку': 1, 'однушки': 1, 'однокомнатную': 1, 'двушку': 2, 'двушки': 2, 'двухкомнатную': 2,
  'трешку': 3, 'трёшку': 3, 'трехкомнатную': 3, 'трёхкомнатную': 3, 'четырехкомнатную': 4, 'четырёхкомнатную': 4,
  ...conventionValues('rooms'),   // v20: bilocale/trilocale, 투룸/쓰리룸 (ROOM_CONVENTIONS)
}
// v21: the bedroom twin — words that ARE a bedroom count (the Arabic dual). Same recognizer shape.
export const BEDROOM_VALUE_WORDS = { ...conventionValues('bedrooms') }

// ── area units (number + one of these → area m²) ─────────────────────────────
export const AREA_UNITS = ['m2', 'm²', 'sqm', 'sq.m', 'sq m', 'square meters', 'square metres', 'კვმ', 'კვ.მ', 'კვ.მ.', 'კვადრატი', 'კვადრატული',
  // v21: Cyrillic and the other site languages ("80 м2" left the whole number as residual)
  'м2', 'м²', 'кв.м', 'кв.м.', 'кв м', 'квм', 'квадратов', 'qm', 'metrekare', 'mkare',
  // v25 (2026-09-29 owner test): the ways Georgians write it on a Latin keyboard, and the slash form
  'კვ/მ', 'კვ.მ²', 'კვადრატიანი', 'kv.m', 'kv.m.', 'kv m', 'kv. m', 'kv m2', 'kv.m2', 'kvm', 'kv/m', 'kvadrati', 'kvadratuli',
  // v29 (2026-10-22): the Georgian-letter forms — "100-150 მ² ბინა" was a PRICE of 100–150, "100 მ2" plain text
  'მ2', 'მ²', 'კვ. მ', 'კვ. მ.', 'კვ მ', 'კვ.მეტრი', 'მეტრი', 'მეტრიანი', 'კვადრატული მეტრი', 'კვ. მეტრი']
// v25: unit words that carry the bound themselves ("100 კვადრატამდე" = up to 100 m², "50 კვ.მ-დან" = from 50 m²)
export const AREA_UNITS_UPTO = ['კვადრატამდე', 'კვ.მ-მდე', 'კვმ-მდე', 'მ2-მდე', 'მეტრამდე', 'kvadratamde', 'kv.m-mde', 'm2-mde']
export const AREA_UNITS_FROM = ['კვადრატიდან', 'კვ.მ-დან', 'კვმ-დან', 'მ2-დან', 'მეტრიდან', 'kvadratidan', 'kv.m-dan', 'm2-dan']
// v21: bounds on an AREA ("at least 100 m²", "up to 60 sqm") — only ever read when an area unit follows,
// so these can include words that would be too loose on their own.
export const AREA_MIN_WORDS = ['at least', 'min', 'minimum', 'from', 'over', 'more than', 'მინიმუმ', 'არანაკლებ', 'от', 'не менее', 'больше', 'ab', 'mindestens', 'en az']
export const AREA_MAX_WORDS = ['up to', 'upto', 'under', 'max', 'maximum', 'less than', 'at most', 'მაქსიმუმ', 'до', 'не более', 'меньше', 'bis', 'höchstens', 'en fazla']

// ── price direction words ─────────────────────────────────────────────────────
// BEFORE or AFTER a bare money amount they decide min vs max.
// A money amount with NO direction word defaults to MAX (people state budgets).
export const PRICE_MAX_WORDS = ['under', 'below', 'max', 'up to', 'upto', 'less than', 'at most', 'მდე', 'ამდე', 'მაქსიმუმ', 'ფარგლებში',
  'до', 'не дороже', 'максимум', 'bis', 'unter', 'altında',
  'ზე ნაკლები', 'ზე ნაკლებ', 'ზე იაფი', 'ნაკლები']   // v29: "800-ზე ნაკლები" (less than 800) was not read at all   // v21: ru "до 200 000" was left over (B-LANG-1)
export const PRICE_MIN_WORDS = ['over', 'above', 'from', 'min', 'at least', 'more than', 'დან', 'მინიმუმ', 'ზემოთ',
  'от', 'минимум', 'ab', 'üzeri',
  'ზე მეტი', 'ზე მეტ', 'ზე ძვირი']   // v29: "200k-ზე მეტი" (more than 200k) was a MAX price

// ── budget markers (v8, engine v12) ──────────────────────────────────────────
// A word that says "the next number is money" without giving a direction —
// "ფასი 200000" / "ბიუჯეტი 150000" compile as a budget (→ max, same default as
// any undirected amount). Found in the 2026-07-18 hunt: these everyday phrasings
// produced no price filter at all.
export const PRICE_BUDGET_WORDS = ['ფასი', 'ფასად', 'ფასში', 'ბიუჯეტი', 'ბიუჯეტით', 'ღირს', 'budget', 'price', 'цена',
  'бюджет', 'бюджетом']   // v27: ru "бюджет 100000" read the price and left "бюджет" over
// v27 (2026-09-30, reviewer finding): the small words people put between the budget word and the amount — "budget OF
// 150000", "ბიუჯეტი ARIS 150000", "budget AROUND 120000". Only read right after a budget word, never on their own.
// (Direction words — "budget up to", "бюджет до" — were already read by the direction group that follows.)
export const PRICE_BUDGET_GLUE = ['of', 'is', 'around', 'about', 'approx', 'approx.', 'approximately', 'roughly', '~',
  'არის', 'დაახლოებით', 'около', 'примерно', 'где-то']

// ── money multipliers (v8, engine v12) ───────────────────────────────────────
// Suffix word/letter that scales the adjacent amount AND marks it as money.
// Replaces the engine's hardcoded [km]: "250კ", "150 ათასი", "1.5 მილიონი",
// "2 მილიონამდე" all failed before v8.
export const MONEY_MULTIPLIERS = {
  'k': 1e3, 'კ': 1e3, 'ათასი': 1e3, 'ათას': 1e3, 'ათასამდე': 1e3, 'тыс': 1e3, 'тысяч': 1e3,
  'm': 1e6, 'მლნ': 1e6, 'მილიონი': 1e6, 'მილიონ': 1e6, 'მილიონამდე': 1e6, 'млн': 1e6,
}

// ── currency markers ──────────────────────────────────────────────────────────
// v8: instrumental/adverbial inflections added — "250000 დოლარად" ("for X dollars")
// is THE standard sale-price phrasing and compiled to nothing before.
export const CURRENCY_WORDS = {
  USD: ['$', 'usd', 'dollar', 'dollars', 'dolari', 'dolaramde', 'dolarit', 'dolarad', 'დოლარი', 'დოლარამდე', 'დოლარით', 'დოლარად', 'დოლარებში', 'долларов', 'доллар', 'доллара', 'дол'],
  GEL: ['₾', 'gel', 'lari', 'laramde', 'larit', 'larad', 'ლარი', 'ლარამდე', 'ლარით', 'ლარად', 'ლარებში', 'лари', 'лар'],   // v21: "1200 лари" (N12)
}

// ── Georgian number words (for "ორ ოთახიანი" — "two-room") ────────────────────
export const NUMBER_WORDS = {
  'ერთ': 1, 'ერთი': 1, 'ორ': 2, 'ორი': 2, 'სამ': 3, 'სამი': 3,
  'ოთხ': 4, 'ოთხი': 4, 'ხუთ': 5, 'ხუთი': 5, 'ექვს': 6, 'ექვსი': 6,
  'შვიდ': 7, 'შვიდი': 7, 'რვა': 8, 'ცხრა': 9, 'ათ': 10, 'ათი': 10,
  // v9: ordinals — "პირველი სართული"/"მეხუთე სართულზე" had no floor filter before.
  'პირველ': 1, 'პირველი': 1, 'მეორე': 2, 'მესამე': 3, 'მეოთხე': 4, 'მეხუთე': 5,
  'მეექვსე': 6, 'მეშვიდე': 7, 'მერვე': 8, 'მეცხრე': 9, 'მეათე': 10,
  // v19 (F-100 audit): English ordinals — "first floor" compiled to nothing while პირველი სართული did.
  'first': 1, 'second': 2, 'third': 3, 'fourth': 4, 'fifth': 5,
  'sixth': 6, 'seventh': 7, 'eighth': 8, 'ninth': 9, 'tenth': 10,
}

// ── query-side district/POI stopwords (v7, engine v11) ───────────────────────
// Everyday Georgian words that COLLIDE with district/POI names and used to compile
// to a confident wrong 📍 chip (found in the 2026-07-18 bug hunt: "ვერ ვპოულობ" →
// Vera, "ვეძებ ბინას" → Vedzisi, "დიდი ბინა" → Didi Digomi, "100 კვადრატული მეტრი" →
// Metromsheni, "ვაკის ბაღი" → Bagebi…). Two lists because the engine blocks them
// differently:
//
// GENERIC_PLACE_WORDS — blocked at STEM level: the word (any inflection) never
// resolves a district/POI BY ITSELF. The compound names these words come from
// ("ძველი თბილისი", "დიღმის მასივი", "ნუცუბიძის პლატო"…) still match as full
// adjacent phrases via the engine-v11 phrase pass, and each compound stays
// reachable through its distinctive part (დიღომი, ნუცუბიძე…).
export const GENERIC_PLACE_WORDS = [
  'მასივი',   // generic "massif" — ≥3 places (Dighmis/Varketili/Gldani masivi)
  'პლატო',    // generic "plateau" — Nutsubidze/Varketili plato
  'თბილისი',  // the whole city — must never pin to Old Tbilisi
  'ძველი',    // "old" — ძველი ბინა is an apartment ask, not Old Tbilisi
  'დიდი',     // "big" — დიდი ბინა/ეზო, not Didi Digomi
  'პატარა',   // "small" — not Patara Digomi
  'ბაღი',     // "garden/park" — ვაკის ბაღი etc; folds to ბაგები otherwise
  'მეტრი',    // "meter" — კვადრატული მეტრი; prefix of მეტრომშენი otherwise
  'ეზო',      // "yard" — a POI is literally named ეზო in the gazetteer
  'old', 'tbilisi', 'didi', 'patara', 'plato', 'masivi',   // latin twins from name_en/aliases
  // F-101 (2026-09-10): ROAD-TYPE WORDS. Seven districts are named after an avenue ("ვაჟა-ფშაველას
  // გამზირის VII კვარტალი" / "Vazha-Pshavela Avenue 7th Quarter"), so the bare word გამზირი / avenue
  // resolved to one of them — "ჭავჭავაძის გამზირი" and "chavchavadze avenue" both compiled to a
  // Vazha-Pshavela quarter with the street name left as residual: a wrong filter, applied silently.
  // A type word identifies nothing on its own (the street matcher's own rule); the full district
  // name still resolves through the phrase pass, exactly as ძველი თბილისი does.
  'გამზირი', 'ქუჩა', 'ჩიხი', 'გასასვლელი', 'კვარტალი', 'მოედანი',
  'avenue', 'ave', 'street', 'lane', 'alley', 'quarter', 'square', 'boulevard', 'blvd',
  // v22: the Russian district names are indexed now (engine v28); their generic words, measured across the 366
  // name_ru values ("Старый Тбилиси" made "ваке тбилиси" pin Old Tbilisi until these were listed)
  'тбилиси', 'старый', 'квартал', 'массив', 'массива', 'плато', 'проспект', 'проспекта', 'гора', 'квемо', 'земо',
  'верхнего', 'край', 'диди', 'патара', 'улица', 'переулок', 'площадь',
]
// COMMON_WORD_HOMONYMS — blocked as EXACT query words only. Their stems ARE the
// district's own stem (ვერ↔ვერა, ვეძ↔ვეძისი), so stem-blocking would kill the
// real district; the bare everyday form is blocked, every inflected place form
// (ვერაზე, ვერას, ვეძისში) still resolves.
export const COMMON_WORD_HOMONYMS = [
  'ვერ',              // negation "can't" — collides with ვერა (Vera)
  'გარდა',            // v29 (2026-10-22): "except" — prefix-matched Gardabani ("გარდა ვაკისა" searched Gardabani AND Vake)
  'ვეძებ', 'ვეძებთ',  // "I'm/we're looking for" — collides with ვეძისი (Vedzisi)
]

// ── search stopwords (v10, engine v14) ───────────────────────────────────────
// Linguistic glue that carries NO filter meaning and must not survive into the
// residual TEXT SEARCH — the residual is AND-per-token against the address
// haystack, so one stopword ("რაიონში", "ან") silently zeroes the whole query.
// Consumed words are still logged in `failures` with reason 'stopword', so the
// query log stays a faithful record of what was typed.
export const SEARCH_STOPWORDS = [
  'რაიონი', 'რაიონში', 'რაიონის',   // "district" — every "X რაიონში" query carried it
  'უბანი', 'უბანში', 'უბნის',        // "neighborhood"
  'ახლოს', 'მახლობლად', 'გვერდზე',   // "near/nearby" — proximity is the chip's job
  'და', 'ან', 'თუ',                  // and / or
  'and', 'or', 'in', 'near',
  'в', 'на', 'и', 'или', 'район', 'районе',
  // v24 (2026-09-29 public test): English and Russian glue words, and the names of OTHER sites — "zillow rentals",
  // "авито снять квартиру": the site name says where the person usually looks, never what they want
  'for', 'the', 'a', 'an', 'to', 'of', 'with', 'me', 'my', 'at', 'on', 'by',
  'с', 'у', 'от', 'для', 'за', 'по', 'к', 'из', 'со', 'во', 'мне', 'рядом', 'возле', 'около',
  'ტიპის', 'ტიპი', 'da', 'akhlos', 'axlos', 'tan', 'ge', 'com',
  // v25: "ჩეხური პროექტის ბინა" — the project TYPE is the filter; the word "project" beside it adds nothing
  'პროექტი', 'პროექტის', 'პროექტით', 'proeqti', 'proeqtis', 'proekti', 'проект', 'проекта',
  // v29 (2026-10-22): modifier words left over after their number was read — each zeroed the AND text search
  // ("5/9 სართული", "1200$ თვეში", "მინიმუმ 3 ოთახი"); cheap/expensive have no sort to map to, so they are dropped
  'სართული', 'სართულზე', 'თვე', 'თვეში', 'თვეა', 'თვიურად', 'month', 'monthly', 'месяц',
  'cheap', 'იაფი', 'იაფად', 'ძვირი', 'expensive', 'дешево', 'дешевая', 'дешевую',
  'მინიმუმ', 'მაქსიმუმ', 'minimum', 'maximum', 'between', 'между',
  // v30 (2026-10-28): "in" and possessive particles of the site's other languages ("أرض في غوري", "ゴリの土地", "哥里的土地")
  'في', 'の', '的', 'im',
  'zillow', 'redfin', 'trulia', 'craigslist', 'realtor', 'reddit', 'avito', 'авито', 'циан', 'cian', 'яндекс', 'yandex', 'place',
]

// ── "the center" (v12, engine v16) ───────────────────────────────────────────
// "ცენტრში ბინა" is one of the most common asks and compiled to nothing — "center"
// isn't a district, it's a BUSINESS DEFINITION of which districts count as central
// Tbilisi. Hand-editable set; compiles to one 🏙 token patching districtCodes with
// ALL of these (OR semantics, same as a multi-district pick). Deliberately the
// historic core only — Vake/Saburtalo are prestige districts, not "ცენტრი" in
// local usage. Tune here as agents push back.
// NOTE: 'ცენტრალური' ("central", the adjective — central heating) is deliberately
// NOT a center word.
// v19 (F-100 audit): WHERE THE ADVERT IS LISTED, typed. "myhome", "ss.ge", "korter" were 0.5% of residuals; the
// facet exists, so the word compiles to the same `sources` array the facet writes. Keys are listing.source
// values; 'manual' is the platform-less row (senTavus, F-099). Two-letter forms are deliberately absent ("ss"
// alone, "fb" alone) — a bare pair of letters is more often a typo than a portal.
export const SOURCE_WORDS = {
  'myhome.ge': ['myhome', 'myhome.ge', 'my home', 'myhome ge', 'my home ge', 'მაიჰომი', 'მაიჰოუმი', 'майхоум'],
  'ss.ge':     ['ss.ge', 'ss ge', 'სს.ჯი', 'ესეს'],
  'korter.ge': ['korter', 'korter.ge', 'korter ge', 'კორტერი', 'კორტერ', 'кортер'],
  'home.ge':   ['home.ge', 'home ge'],   // v24: "home ge qira" matched a complex called B-Home
  'bog.ge':    ['bog.ge'],
  'facebook':  ['facebook', 'ფეისბუქი', 'ფეისბუქ', 'фейсбук'],
  'manual':    ['sentavus', 'სენტავუსი', 'სენტავუს', 'сентавус'],
}
export const SOURCE_LABELS = { 'myhome.ge': 'MyHome', 'ss.ge': 'SS.ge', 'korter.ge': 'Korter', 'home.ge': 'Home', 'bog.ge': 'Bog', 'facebook': 'Facebook', 'manual': 'senTavus' }

export const CENTER_WORDS = ['ცენტრი', 'ცენტრში', 'ცენტრთან', 'ცენტრის', 'centri', 'downtown', 'center', 'centre', 'центр', 'центре',
  'centrshi', 'centreshi', 'centresh', 'centrze', 'centrtan', 'centris', 'в центре']   // v24: Latin-typed Georgian cases
export const CENTER_DISTRICT_CODES = ['TBS_OLD', 'TBS_MTATSMINDA', 'TBS_VERA', 'TBS_SOLOLAKI']

// Engine v20 — the city the agency actually works in (2026-07-31).
// ⚠️ Tbilisi is the ONLY Georgian city with no row in `district`: every other city is a
// root-level MUN_/TWN_ code (79 of them — Batumi, Kutaisi, Gori, Bakuriani…), because
// Tbilisi was modelled as the IMPLICIT root that the 63 TBS_* districts hang under. So the
// city recognizer had nothing to match and the single most-typed word in the box fell
// through to free-text: `თბილისი` is the #1 unclaimed fragment in search_query_log, 40
// occurrences in three weeks, ~5× the next one.
// As free text it was actively harmful — AND-per-token against the address haystack, where
// 3,393 of 28,725 properties (12%) do NOT carry the city in address_text, so typing your own
// city silently excluded them.
// ⚠️ Codes are DERIVED from the live district rows (TBS_/PERI_ prefixes), never listed here:
// a hardcoded set of 63 would rot the first time a district is added, and the compiler's
// contract is that it can only ever emit a filter the panel can also show.
export const TBILISI_WORDS = ['თბილისი', 'თბილისში', 'თბილისის', 'tbilisi', 'tbilisში', 'тбилиси', 'тбилисси',
  // v24: Latin-typed Georgian cases — "tbilisshi" resolved to OLD Tbilisi
  'თბილისთან', 'თბილისიდან', 'თბილისზე', 'tbilisshi', 'tbilishi', 'tbilisis', 'tbilisidan', 'tbilistan', 'tbilisze', 'tbilisamde', 'thbilisi',
  // v22: the site's other languages and the historic name (no district row carries them)
  'tiflis', 'тифлис', 'tbilissi', 'تبليسي', 'تفليس', 'トビリシ', '第比利斯']
export const TBILISI_CODE_PREFIXES = ['TBS_', 'PERI_']

// ── restriction subjects (v14, engine v18) ───────────────────────────────────
// The sidebar's Terms facet has ~46 chips (feature.category = 'tenant_terms') and NONE
// of them were words the box understood: typing "ინდოელები" fell through to free text,
// got trimmed to "ind" by the suggest arm, and came back with Mtatsminda Park. The
// SUBJECT LIST itself is NOT duplicated here — it comes from the DB, same rows the
// chips render (feature-names-are-matching-keys: those names are the vocabulary).
// This map is only the EXTRA words: inflections the stemmer misses, latin/russian
// spellings, and the everyday synonym people actually type.
//
// Meaning of a match: the agent is describing THEIR CLIENT ("my client has a dog"),
// which is the `hide` mode — drop listings with a recorded not_allowed stance on that
// subject, unrecorded passes. See RESTRICT_MODES in deskFeed.js. The opposite reading
// ("show me flats that ban dogs") is never what someone types into a search box.
//
// ⚠️ COLLISION RULE, same as DISTRICT_ALIASES: never add a word that is ordinary
// Georgian glue or a place name. Each of these is matched as a WHOLE word (single
// words additionally through the stemmer), so a generic entry would hijack every
// query containing it.
export const RESTRICTION_ALIASES = {
  // people — the group the client belongs to
  pol_indians:     ['ინდოელი', 'ინდური', 'indian', 'indians', 'индийцы'],
  pol_girl:        ['გოგონა', 'ქალი', 'girl', 'gogo'],
  pol_boys:        ['ბიჭი', 'boy', 'boys'],
  pol_family:      ['ოჯახური', 'family', 'семья'],
  pol_working_couple: ['couple', 'пара'],
  pol_children:    ['ბავშვი', 'child', 'kids', 'дети', 'ребенок'],
  pol_students:    ['სტუდენტი', 'student', 'студенты'],
  pol_smoker:      ['მწეველები', 'smoker', 'курящий', 'smokers', 'smoking', 'მოწევა', 'курящие', 'курить', 'курение'],   // v31: the act, not only the person — "smoking allowed" / "მოწევა დაშვებულია" / "можно курить" compiled nothing
  // The three ენოვანი words were their own subjects until 0400. Agents were using them
  // to answer the NATIONALITY question, so the codes were reassigned and deactivated —
  // and their words moved here rather than being dropped, because the words are what
  // people actually type and losing them would silently shrink the search.
  pol_foreigners:  ['უცხოელი', 'უცხოური', 'foreigner', 'foreigners', 'иностранцы',
                    'ინგლისურენოვანი', 'english speaking'],
  pol_georgians:   ['ქართველი', 'georgian', 'ქართულენოვანი', 'georgian speaking'],
  pol_russians:    ['რუსი', 'russian', 'რუსულენოვანი', 'russian speaking'],
  pol_turks:       ['თურქი', 'turk', 'turkish'],
  pol_muslims:     ['მუსლიმი', 'muslim'],
  pol_ukrainians:  ['უკრაინელი', 'ukrainian'],
  pol_iranians:    ['ირანელი', 'iranian'],
  pol_chinese:     ['ჩინელი', 'chinese'],
  pol_arabs:       ['არაბი', 'arab'],
  // ⚠️ pol_rus_ukr_bel deliberately has NO alias: its DB name is the slash-form
  // "რუსი / უკრ. / ბელ.", and its only natural single word (რუსი) belongs to
  // pol_russians above. A combined subject needs a phrase people actually type —
  // check the query log before inventing one.
  // animals
  pol_animals:     ['ცხოველი', 'pet', 'pets', 'animal', 'животные', 'pet friendly', 'pet-friendly', 'pets allowed', 'with pets', 'с животными', 'можно с животными', 'ცხოველით'],   // v21: the phrase, not 'pet' + a leftover 'friendly'
  pol_cat:         ['კატები', 'cat', 'кошка', 'кот'],
  pol_dog:         ['ძაღლები', 'dog', 'собака'],
  // money / status conditions — "hide listings that refuse this" is the same sentence
  // ⚠️ B302: the three PAYMENT words (გადარიცხვა / კრიპტო / განვადება) are deliberately GONE,
  // not repointed. 0400 deactivated their pol_* codes — payment became listing_terms columns
  // (payment_currencies / financing_accepted) — and a restriction filter on a code with zero
  // evidence rows does not "match nothing": every cluster reads no_evidence → unknown, so one
  // typed word shelved the ENTIRE corpus onto may-consider and served an empty result. An
  // unclaimed fragment is strictly better: it logs to search_query_log, which is how the
  // vocabulary learns. Wiring the words to the terms columns is a real client-side filter
  // (~9 sites; the Desk has no filter registry — B261) and stays open on B302.
  pol_residency:           ['ბინადრობა', 'residence permit'],
  pol_remote_worker:       ['დისტანციური', 'remote'],
  // pol_lang_english / pol_lang_russian removed 0400 — their words moved onto
  // pol_foreigners / pol_russians above, where the codes now project.
}
// A subject alone does not say WHICH of the two filter intents you mean (owner, 2026-07-28:
// "ინდოელები must have two values — are they accepted or not accepted"). Both are "my client
// is X"; they differ in strictness, and the difference is the size and confidence of the
// result set:
//   hide  (default, bare subject) — drop listings RECORDED as forbidding X. Unrecorded
//                                   passes. The everyday act, and the large set.
//   allow (these words)           — keep ONLY listings recorded as allowing X. Unrecorded
//                                   does NOT pass. A small, confident shelf.
// A word from this list adjacent to a subject switches it to `allow`; the word is consumed
// with the subject, so it never survives into the residual text search.
// ⚠️ There is deliberately NO word for "show me listings that FORBID X". That is the one
// reading RESTRICT_MODES rules out: a filter labelled with a stance shows the flats that
// reject your client — the opposite of the intent, and a mistake an agent makes once a day.
// ⚠️ 'მხოლოდ' was here and was REMOVED (owner, 2026-07-28: "მხოლოდ ინდოელები is wrong
// wording"). In this market "მხოლოდ ინდოელები" is the AD's own `only` stance — the owner
// rents exclusively to Indians — not the searcher's "only where Indians are accepted".
// Same trap as labelling a filter with a stance: it reads as a statement about the
// listing when it is meant as a statement about the client. English 'only' is dropped
// for the identical ambiguity.
export const RESTRICTION_ALLOW_WORDS = [
  'დაიშვება', 'დაშვებული', 'დაშვებულია', 'იღებენ', 'ღებულობენ',
  'allowed', 'accepted', 'accepts',
  'разрешено', 'можно',
]
// The third intent (owner, 2026-07-28: "user also must be able to search for
// restrictions") — find the listings that DO restrict this subject. This is the stance
// lens RESTRICT_MODES previously refused to offer, and the refusal was right for a
// chip labelled with a bare subject; it is a legitimate question when the agent asks
// for it in words. Negation first: 'არ' sits between subject and verb ("ინდოელები არ
// დაიშვება"), so the adjacent word is the negation, not the verb.
export const RESTRICTION_DENY_WORDS = [
  'არ', 'არა', 'აკრძალული', 'აკრძალვა', 'შეზღუდვა', 'შეზღუდული',
  'forbidden', 'banned', 'restricted', 'restricts',
  'нельзя', 'запрещено',
]
// ── the words that say a FEATURE IS ABSENT (v26, 2026-09-30) ──────────────────────────────────────
// "ლიფტის გარეშე" (without a lift), "without a balcony", "no lift", "ლიფტი არ აქვს" (has no lift). Before this list the
// compiler read the feature word and ignored the negation around it, so all of them searched for flats WITH the feature
// and left "გარეშე" as unread text — the exact opposite of what was asked, for every feature (lift, balcony, furniture,
// parking). The negation word is consumed with the feature, so it can never survive into the residual text search.
//   BEFORE  a word standing before the feature ("without lift", "no balcony", "არა ლიფტი", "без лифта")
//   AFTER   a word standing after it: Georgian puts "without" AFTER the noun ("ლიფტის გარეშე")
//   VERBS   "არ" + one of these after the feature ("ლიფტი არ აქვს", "ლიფტი არ არის") — the verb is consumed too
//   ARTICLES  skipped between a BEFORE word and the feature ("without A lift", "no ANY lift")
// ⚠️ 'არ' alone is NOT a BEFORE/AFTER word here: "ლიფტი არ" with no verb says nothing, and "არ" before a noun is not
// "without" in ordinary Georgian. The restriction recognizer has its own (tenant) deny list — a different question.
export const FEATURE_NEGATION_BEFORE = ['without', 'no', 'non', 'not', 'არა', 'без']
export const FEATURE_NEGATION_AFTER = ['გარეშე']
export const FEATURE_NEGATION_VERBS = ['აქვს', 'აქვთ', 'არის', 'არაა']
export const FEATURE_NEGATION_ARTICLES = ['a', 'an', 'the', 'any']
// Subjects the box must NOT compile even though they are tenant_terms rows: these two
// describe the AD's own restriction posture, not the client. "Hide listings that forbid
// 'no restrictions'" is not a sentence anyone means.
export const RESTRICTION_NON_SUBJECTS = ['terms_no_limits', 'terms_standard']

// ── browse-worthy property features (v15, engine v19) ────────────────────────
// B37's amenity half. The RPC has accepted `p_feature_codes` since 0241 and nothing
// could drive it, because adding the WORDS first would compile chips the panel cannot
// represent (SEARCH_ARCHITECTURE §15: "the vocabulary is the last step, not the first").
// This list is that panel — ONE list drives the sidebar facet AND the search box, so the
// box can never understand a filter the panel cannot show, or the reverse.
//
// ⚠️ SUBSET RULE. A code here must exist in the live `feature` table. An unmapped code
// returns 0 matched and shelves the WHOLE corpus as unknown (measured 2026-07-28) — a
// silent whiteout that reads as a finding about the market. Both ends guard it: the panel
// renders only codes present in the fetched vocabulary, and the server rejects an unknown
// code outright rather than answering a question it did not understand.
//
// ⚠️ NOT a dump of the ~321-tag registry. Excluded on purpose:
//   • the EXCLUSIVE axes — building age/class, renovation, frame, project (see
//     featureAxes.js). Those are ladders where a second value means the sources disagree;
//     an AND-ed "must have" checkbox is the wrong instrument for them.
//   • tenant_terms — that vocabulary is the Terms facet's, filtered by stance, not presence.
//   • floor_top and layout_studio — the panel already has a top-floor control, and studio's
//     omission from the room vocabulary is a deliberate market judgement (see ROOM_WORDS).
//
// Order is display order within each group; groups render in FEATURE_GROUPS order.
// Counts in the comments are `present=true` rows over properties with an active listing,
// measured 2026-07-29 — they are why each code is here, and the first thing to re-check
// if one stops earning its row.
export const FEATURE_GROUPS = [
  // Comfort — what the flat itself has.
  // room_isolated_kitchen joined 2026-08-27 (ecosystem audit): 1,475 properties carry it,
  // ⚠️ The REGISTRY files it under `planning`/`room`, and the derived panel therefore offers it
  // under დაგეგმარება — which is where it now lives after the owner had its hand-placed twin
  // removed from დეტალები. This fallback list keeps it in comfort only because the fallback has
  // no planning group and fires ONLY when the registry cannot inform the panel at all; in that
  // state there is no second rendering to disagree with. If a planning group is ever added here,
  // this code moves into it.
  // the FILL FORM asks clients for it, and it was the largest stored-but-unfilterable code.
  ['comfort', ['furnished', 'balcony', 'room_isolated_kitchen', 'air_conditioner', 'storage_room', 'cellar', 'fireplace']],
  // Outdoor private space — the three Idmon reads that had no facet home (audit 2026-08-27).
  // Same subset rule as every group: a code renders only if the live feature table holds it.
  // 📌 BOTH SESSIONS FOUND `loggia` THE SAME DAY, INDEPENDENTLY — 956 properties carried it, it
  // had been in the registry and in Idmon's write list all along, and no panel could ask for one.
  // The other session added it beside `balcony`, then WITHDREW its copy rather than merge a
  // duplicate (two rows for one code renders the checkbox twice and doubles it into
  // SEARCH_FEATURE_CODES), leaving a comment that the gap was this branch's to close. This merge
  // is that closing. Recorded because the reconciliation is the interesting part: the duplicate
  // was caught by a human reading the other branch, not by any gate.
  ['outdoor', ['terrace', 'veranda', 'loggia']],
  // Building — what the address has. Parking is effectively present-only in the corpus
  // (2,711 yes vs 16 no), so an unchecked parking box is silence, never a "no".
  ['building', ['elevator', 'parking', 'garage', 'garage_underground', 'amen_concierge', 'sec_guard']],
  // Utilities — the connected services. utility_gas/hot_water carry thousands of explicit
  // FALSE rows, so these are the codes where the mismatch verdict does real work.
  ['utilities', ['heat_central', 'utility_hot_water', 'utility_gas', 'utility_internet', 'utility_ventilation']],
  // Appliances — rental-side asks; a furnished flat is not automatically an equipped one.
  ['appliances', ['washing_machine', 'fridge', 'dishwasher', 'builtin_kitchen']],
  // Occupancy — the flat's history, not its equipment. One code, its own group, because
  // უცხოვრებელი fits none of the rows above and folding it into comfort would read as an
  // amenity. B836's UI half (owner: "And we need that filter in here"): the code was minted
  // OUTSIDE the renovation ladder (0663) precisely so it can co-exist with ახალი რემონტი, and
  // this facet ANDs, so ticking both asks for the flat that is both — which exists (the corpus
  // sentence that proved it: "სრულად გარემონტებული და უცხოვრებელი"). Present-only in practice
  // (Idmon writes true only), so an unchecked box is silence, never a "no" — the parking rule.
  ['occupancy', ['occupancy_never_lived_in']],
  // ── Defects (B1021, 2026-08-27) — and the ONE group here that is not an amenity. ──────
  //
  // Owner request, review 40, on damp and cracking he could see in the photographs:
  // "there is NO FIELD for it need to add it in field". Migration 0745 minted the four
  // words and wired Lynceus's `defects_observed` through the promotion registry.
  //
  // ⚠️ READ THE SEMANTICS BEFORE TOUCHING THIS GROUP. Every other row above answers
  // "I want a flat that HAS this". A tick here asks for a flat where a defect WAS
  // RECORDED — which is a triage question (what needs a price argument, what needs a
  // second look), not a shopping one. Nobody is searching for mould. The facet ANDs and
  // is presence-only, so this is the only meaning the engine can honour; the group's own
  // note says so on screen rather than leaving an agent to infer it.
  //
  // 🔴 AND THE FILTER AN AGENT ACTUALLY WANTS IS THE ONE THAT DOES NOT EXIST: "hide the
  // flats with recorded damp". `p_feature_codes` has no negative verdict — the amenity
  // comment above says the box must not pretend to offer one — so exclusion is a
  // separate build, filed rather than faked. See the backlog.
  //
  // ⚠️ THESE READ ZERO TODAY and that is not a fact about the market. The worker
  // carrying `defects_observed` is committed and NOT deployed, and Lynceus is frozen
  // (B896) — exactly one stored observation carries a defect claim, from a review run.
  // The zero-supply rule stated above ('a code returns to this list when it returns with
  // data') is deliberately overridden here on the owner's instruction to wire it ahead of
  // the data, and the group note is what stops the empty answer reading as 'no such
  // flats exist'.
  ['defects', ['damp_stain', 'mould', 'peeling_paint', 'cracked_surface']],
]
export const SEARCH_FEATURE_CODES = FEATURE_GROUPS.flatMap(([, codes]) => codes)

// ── THE PANEL IS DERIVED NOW, NOT LISTED (2026-08-27, after the live usability test) ────────
// The list above stopped being the gate and became the CURATED CORE: codes that always show,
// in that order, because they are what agents ask for most. Everything else is derived from
// the same registry the backend uses — no second vocabulary, and no whitelist to go stale.
// The test that forced this: typing „აუზი" (pool) did nothing, although 2,919 properties
// carry the concept. It was never missing from the system; it was missing from a hand-typed
// array, and 26 of 422 active codes were reachable.
//
// THE RULE, in order — each clause is a measured reason, not a preference:
//   1. active, and present in the live registry (the pre-existing subset rule: an unmapped
//      code shelves the WHOLE corpus as unknown, so this end can never invent one).
//   2. NOT on an exclusive axis — axisOf() is the canonical answer (featureAxes.js), so
//      renovation/frame/project/building-age/apartment-class stay with their ladder controls.
//      Deriving by PREFIX is exactly what featureAxes.js exists to stop; this asks it instead.
//   3. NOT tenant_terms — that vocabulary belongs to the Terms facet, filtered by STANCE
//      (allowed/forbidden), never by presence. An AND-ed "has pets" box is a category error.
//   4. usage >= MIN_USAGE, from the registry payload (0750). 305 of 422 active codes have no
//      rows at all; offering those is a panel of guaranteed-empty answers. ⚠️ usage counts
//      present=TRUE only — `pool` carries 24,700 present=false portal checkboxes against 165
//      real ones, so a naive row count would rank the emptiest concepts first.
//   5. minus DERIVED_DENY — the two codes that already have their own control.
// Groups are the registry's own `category`, so a new category appears without a code change.
// 300 was a guess and it was wrong, caught by the owner reading the panel: the whole
// View & orientation section rendered ONE entry, "ხედი შიდა ეზოზე", which reads as a bug.
// Measured 2026-08-27 — of 18 view/orientation codes only four carry any data at all
// (view_courtyard 300, view_city_or_mountain 231, orient_south 160, panoramic_windows 112)
// and the bar landed exactly on the boundary, hiding three useful filters to show one.
// Across the whole registry the real cliff is between HAVING data and not: 107 codes carry
// rows, 223 carry none. Moving the bar 300 → 50 adds only 14 codes (66 → 80) and recovers
// the populated ones; a filter that finds 112 properties is useful, one that finds 0 is a
// promise the corpus cannot keep. That is the line this number is drawn on.
const MIN_USAGE = 50
// ✅ FURNISHING DEGREE: THE HOLD IS LIFTED, AND IT IS A LADDER (0764, owner 2026-08-28).
// The degree is three ordinary feature CODES — furn_fully / furn_partial / furn_none — and a flat
// is exactly ONE of them. They were held out of this derived panel by a deny list while they
// carried no rows; the vision promotion landed 2026-08-28 (furn_fully 369 · furn_partial 295 ·
// furn_none 23) and two of the three now clear the bar.
// They are excluded from this panel by AXIS now, not by a deny list — axisOf() returns
// AXIS.FURNISHING and the loop below skips anything on an axis. ONE mechanism decides which codes
// are ladders, so a fourth degree needs no edit here. FurnishingFacet.jsx is the control.
// ⚠️ Do NOT re-add them to DERIVED_DENY "to be safe": that would hide them from the ladder too,
// because the facet reads the same vocabulary this panel does.
export const FURNISH_DEGREE = ['furn_fully', 'furn_partial', 'furn_none']
const DERIVED_DENY = new Set(['floor_top', 'layout_studio'])
// ⚠️ 'utility' is deliberately absent: it and 'utilities' were the same concept and the
// panel rendered TWO sections with the same heading until 0751 merged them in the registry.
// If it reappears here, the registry has regrown the duplicate — fix it there, not here.
const GROUP_ORDER = ['outdoor', 'planning', 'kitchen', 'furniture', 'utilities',
  'building', 'security', 'garage', 'orientation_view', 'land', 'status', 'orientation', 'defect']

// 0759 — WHICH GROUPS THE SELECTED KIND CAN ANSWER AT ALL. Owner, 2026-08-28: "frames dont have
// furniture, land dont have anything that apartments have, so we should control filters
// downstream not everything all the time". Measured first: the panel offered 91 controls to LAND
// and land can answer 10 — 81 boxes that return nothing and read as "no such plots".
//
// RESOLUTION, the cheapest grain that stays reviewable: a code row overrides a category row, and
// the default is APPLICABLE. Category alone is too coarse for "utilities", which holds both the
// site services a plot really has and the building systems it cannot — 0751 merged those
// deliberately, so this overrides per code rather than re-splitting the category.
//
// ⚠️ FAIL OPEN, in both senses. No kinds selected -> every group, because a cross-kind search must
// not hide a control that is legitimate for one of them. No capability rows -> every group, so an
// older server degrades to exactly today's answer. HIDING A REAL FILTER IS WORSE THAN SHOWING A
// THIN ONE: a hidden control is invisible and unrecoverable by the agent, while a thin one returns
// few rows with the possible-matches shelf saying why.
//
// 📌 RE-LANDED 2026-08-28 after being reverted the same day (the Desk went white for one admin).
// The revert said plainly that it was NOT proven to be the cause — the same bundle loaded clean
// for another agent and Vercel was still serving the previous assets — and re-reading this code
// found no path that can throw: every input is defaulted, every lookup returns null rather than
// undefined-dot, and both empty cases return the pre-0759 panel. What was missing was not safety
// but PROOF, so the re-land carries harness cases for every kind instead of the one I sampled.
export function capabilityFilter(kinds, kindFeatures) {
  // 🔴 `Array.isArray`, NOT `kinds || []` — AND THIS IS VERY LIKELY WHY THE DESK WENT WHITE.
  // `(kinds || []).filter` throws "filter is not a function" for a string or an object, and this
  // runs during RENDER of the filter panel, so the throw reaches the tab's error boundary and
  // draws exactly the reported "ამ ხედის ჩატვირთვა ვერ მოხერხდა" — for the ONE agent whose
  // restored state held that shape, on the same bundle that loaded fine for everybody else. That
  // asymmetry was the part nobody could explain, and a per-agent state shape explains it.
  // `filters.kinds` is an array everywhere it is WRITTEN (ADV_DEFAULTS, the URL parser,
  // canonicalFilters' ARRAY_KEYS), which is precisely why the original code trusted it. A cached
  // snapshot in localStorage is not written by any of them.
  // Found by the enumeration this re-land added, not by re-reading the code — I had already
  // re-read it and concluded nothing here could throw.
  const ks = (Array.isArray(kinds) ? kinds : []).filter(Boolean)
  const rows = Array.isArray(kindFeatures) ? kindFeatures : []
  if (!ks.length || !rows.length) return () => true
  const at = (kind, type, value) => {
    const r = rows.find(x => x && x.kind === kind && x.scope_type === type && x.scope_value === value)
    return r ? r.applicable !== false : null
  }
  // The UNION across selected kinds: a control stays if ANY chosen kind can answer it. Searching
  // "apartment or land" must not lose the apartment's questions.
  return (code, category) => ks.some(k => {
    const byCode = at(k, 'code', code)
    if (byCode !== null) return byCode
    const byCat = at(k, 'category', category)
    return byCat === null ? true : byCat
  })
}

export function featureGroupsFrom(vocab, axisOf, allow = null) {
  const rows = Array.isArray(vocab) ? vocab : []
  if (!rows.length) return FEATURE_GROUPS
  // No usage on the payload (older server) → keep exactly today's answer. Degrading to the
  // curated core is always safe; degrading to "everything" would not be.
  if (!rows.some(f => typeof f.usage === 'number')) return FEATURE_GROUPS
  const core = new Set(SEARCH_FEATURE_CODES)
  const byGroup = new Map()
  // One row per code even if the payload repeats one: a duplicate would render twice and
  // collide on the React key. Cheap guard, caught by searchVocab.test.mjs.
  const seenCode = new Set()
  for (const f of rows) {
    const code = f && f.code
    if (!code || seenCode.has(code) || f.active === false || DERIVED_DENY.has(code)) continue
    seenCode.add(code)
    if (f.category === 'tenant_terms') continue
    // 0759: the kind gate. One rule here rather than a decision per code, and a code minted
    // tomorrow inherits its category's row with nobody deciding anything.
    if (allow && !allow(code, f.category)) continue
    // „პანელური რადიატორი აქ აღარ გამოდის ცალკე ფილტრად. ის არის extraction signal."
    // Owner, 2026-08-27 — and it generalises: subcategory='heating_object' is Lynceus's eight
    // LOCATED objects (0590), things a photograph can point at, not questions a client asks.
    // Only panel_radiator clears the usage bar today (161; the rest are 0-6), so this excludes
    // one visible row now and every future one by rule instead of by name.
    // ⚠️ The owner also said it should NORMALISE to ცენტრალური გათბობა. That half is NOT done
    // here: 0590 states "heating.system is a DEDUCTION; the object is the OBSERVATION", and a
    // panel radiator exists on central, gas-boiler and electric systems alike. Mapping it to
    // heat_central would assert a system the object does not prove. Flagged for a ruling.
    if (f.subcategory === 'heating_object') continue
    if (typeof axisOf === 'function' && axisOf(code, rows)) continue
    // DEFECTS ARE EXEMPT FROM THE BAR (owner, 2026-08-27: "do that").
    // The bar exists so the panel never offers a filter the corpus cannot answer. A defect
    // filter answers in the POSITIVE direction truthfully — "show me flats where we have SEEN
    // damage" — and that is a real question for renovation and investment buyers, which is
    // why the category was minted (0745) rather than folded into the condition ladder.
    // ⚠️ Its coverage is tiny and the agent must not read absence as health: 8 rows over 8
    // properties today, every one from Lynceus, which has examined 972 listings of ~49,000.
    // An unticked box says nothing at all here — far more so than for balcony (25,221). The
    // three-population readout ("ემთხვევა / შეიძლება ემთხვეოდეს / გამოირიცხა") already keeps
    // unchecked stock on the shelf rather than in the excluded pile, which is what makes
    // offering this honest rather than a claim about the market.
    // It grows nightly: the defects field shipped days ago and vision runs 03:17.
    const isDefect = f.category === 'defect'
    if (!core.has(code) && !isDefect && (Number(f.usage) || 0) < MIN_USAGE) continue
    if (isDefect && (Number(f.usage) || 0) === 0) continue   // still never an empty answer
    const g = f.category || 'other'
    if (!byGroup.has(g)) byGroup.set(g, [])
    byGroup.get(g).push(code)
  }
  const usageOf = Object.fromEntries(rows.map(f => [f.code, Number(f.usage) || 0]))
  const out = [...byGroup.entries()].map(([g, codes]) => {
    const curated = SEARCH_FEATURE_CODES.filter(c => codes.includes(c))
    const extra = codes.filter(c => !core.has(c)).sort((a, b) => usageOf[b] - usageOf[a])
    return [g, [...curated, ...extra]]
  }).filter(([, codes]) => codes.length)
  out.sort((a, b) => {
    const ia = GROUP_ORDER.indexOf(a[0]), ib = GROUP_ORDER.indexOf(b[0])
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a[0].localeCompare(b[0])
  })
  return out.length ? out : FEATURE_GROUPS
}

// ── The state of the flat — the two ladders (0433 / v37, designer handoff 2026-08-06) ──────
//
// The exclusion note above says ladders are the wrong shape for an AND-ed "must have" box,
// and that is still true — which is exactly why these live in their OWN list with their own
// parameter. Inside this facet every tick is OR'd, including across the two rows: a flat is
// "ახალი რემონტი OR გარემონტებული", never both at once. Same subset rule as FEATURE_GROUPS —
// a code here must exist in the live `feature` table, and the panel renders only the
// intersection, so a retired code disappears instead of shelving the corpus.
//
// ⚠️ ORDER IS THE LADDER, NOT THE SUPPLY. Rows read best → worst, which is why გარემონტებული
// (3,417) sits above ძველი რემონტი (4,691) despite being rarer, and why მწვანე კარკასი leads
// the frames: green is the HIGHEST pre-finish tier — white plus laid central-heating pipes and
// fully distributed internal routing (owner correction, 2026-08-06; the definitions now on
// `feature.description_ka` are the owner's own words, migration 0432). თეთრი პლიუსი sits last
// because it is a developer's marketing term rather than a rung of the standard.
//
// ⚠️ TWO VOCABULARY CATEGORIES, ONE ROW. `renovated` is category `condition` while the rest of
// its row is category `renovation` — a parser accident, not a distinction an agent has. The
// server groups them identically (0433 folds both to one ladder) and featureAxes.js already
// treats condition/renovation/frame as one CONDITION axis, so all three agree by construction.
//
// ⚠️ EIGHT FURTHER CODES EXIST AND CARRY ZERO PROPERTIES (`ახალაშენებული`, `ძველი საბჭოთა`,
// five dead `reno_*`, and `reno_renovated` — a duplicate of `renovated`). They are absent on
// purpose: a control must never present a confident zero, and an agent who clicks one
// concludes the SUPPLY does not exist rather than that the question was never parsed. A code
// returns to this list when it returns with data.
//
// 📌 `frame_needs_work` (სარემონტო) IS THE CASE THAT RULE ANTICIPATED, and it came back.
// It read as zero-supply because the registry labelled it with the slashed compound
// `სარემონტო / კარკასი` while both myhome.ge and ss.ge emit the bare first word, so 863
// listings resolved to nothing and were dropped (B345). 0436 added the alias, 0437 replayed
// the stored observations, and the filter now returns **752 leads** where it returned none.
// The zero was never the market's answer — it was ours. When a rung reads as zero supply,
// check that the token we listen for is the token the portals actually send.
export const CONDITION_LADDERS = [
  ['reno',  ['reno_new', 'renovated', 'reno_old', 'reno_in_progress']],
  // frame_needs_work sits after the worst STANDARD rung and before თეთრი პლიუსი, which stays
  // last as a marketing term. Its registry label is `სარემონტო / კარკასი` — the vocabulary
  // itself equates needing work with the shell, so it belongs beside the shell rungs.
  ['frame', ['frame_green', 'frame_white', 'frame_black', 'frame_needs_work', 'frame_white_plus']],
]
export const CONDITION_CODES = CONDITION_LADDERS.flatMap(([, codes]) => codes)

// 0788-0790 — THE LADDER FROM THE REGISTRY, replacing the constant above when the server sends it.
//
// 🔴 THE CONSTANT HAD DRIFTED MATERIALLY FROM THE OWNER'S RULING, which is the argument for
// deriving it: it offered 9 codes in 2 groups against 5 stages and 13 rungs, and was missing
// მშენებარე (1,410 properties), the კარკასი own bucket, გარემონტებული (2,647), კოსმეტიკური and
// უცხოვრებელი outright. It also grouped სარემონტო with the shells — its comment argued that "the
// vocabulary itself equates needing work with the shell", which the owner overturned by making
// სარემონტო a stage of its own: a flat with old walls HAS been lived in.
//
// Returns the same [group, codes] shape the panel already renders, so nothing downstream changes.
// ⚠️ null/empty ladder -> the constant, byte for byte. That is the pre-0788 panel and the correct
// degradation; a condition facet with no rungs would be worse than a stale one.
// ⚠️ ORDER COMES FROM THE DATA (stage.rank, then rung.rank) and must not be re-sorted here — the
// own bucket sits where its rank puts it, which for გარემონტებული is the MIDDLE of its stage.
export function conditionLaddersFrom(ladder) {
  const stages = Array.isArray(ladder?.stages) ? ladder.stages : []
  const rungs  = Array.isArray(ladder?.rungs)  ? ladder.rungs  : []
  if (!stages.length || !rungs.length) return CONDITION_LADDERS
  const out = []
  for (const st of [...stages].sort((a, b) => (a.rank || 0) - (b.rank || 0))) {
    const codes = rungs
      .filter(r => r.stage === st.stage)
      .sort((a, b) => (a.rank || 0) - (b.rank || 0))
      .map(r => r.code)
    if (codes.length) out.push([st.stage, codes, st])
  }
  return out.length ? out : CONDITION_LADDERS
}

// EXTRA words only — the feature's own name_ka/name_en/name_ru and its DB `aliases` are
// already the vocabulary (feature-names-are-matching-keys), same rule as the restriction
// subjects above. What belongs here: latin transliterations of what people type, common
// misspellings, and the everyday synonym that is not the registry's label.
//
// ⚠️ COLLISION RULE, as for DISTRICT_ALIASES/RESTRICTION_ALIASES: whole-word matching, so
// never add ordinary glue, a place name, or a word another facet owns. Two specific traps:
//   • 'ცენტრალური' is NOT a center word (see CENTER_WORDS) and must not become one — it is
//     half of "ცენტრალური გათბობა", which matches as a two-word phrase and cannot collide.
//   • no bare 2-letter forms ('ac', 'tv'): the index floor is 3 characters precisely so a
//     short token cannot hijack a query.
export const FEATURE_ALIASES = {
  // v25: the everyday names of the Soviet series (the registry holds ხრუშოვკა and the genitive ხრუშჩოვის)
  proj_khrushchovka:   ['ხრუშჩოვკა', 'ხრუშოვკა', 'ხრუშჩოვკაში', 'ხრუშოვკაში', 'хрущёвка', 'хрущевка', 'хрущёвку', 'хрущевку', 'khrushchovka', 'khrushovka', 'xrushovka', 'xrushchovka'],
  proj_czech:          ['chekhuri', 'chexuri', 'чешский проект', 'чешка', 'чешку'],
  proj_kavlashvili:    ['kavlashvili', 'kavlashvilis', 'кавлашвили'],
  // v21: Russian shell words — "белый каркас" matched the unspecified shell and left "белый" over
  frame_white:         ['белый каркас', 'thetri karkasi', 'tetri karkasi', 'white shell'],
  frame_black:         ['черный каркас', 'чёрный каркас', 'shavi karkasi', 'black shell'],
  frame_green:         ['зеленый каркас', 'зелёный каркас', 'mwvane karkasi', 'green shell'],
  cls_under_constr:    ['under construction', 'being built', 'строящийся', 'строящаяся', 'в строящемся', 'mshenebare'],
  cls_new_built:       ['new build', 'new building', 'newly built', 'new built', 'новостройка', 'novostroika', 'axali ashenebuli', 'axalashenebuli'],
  cls_old_built:       ['old build', 'old building', 'older build', 'вторичка', 'старый фонд', 'dzveli ashenebuli'],
  pool:                ['pool', 'swimming pool', 'бассейн', 'აუზი', 'auzi'],
  furnished:           ['ავეჯიანი', 'ავეჯით', 'avejit', 'avejiani', 'меблированная'],
  balcony:             ['აივნით', 'აივნიანი', 'aivani', 'balkoni', 'балкон'],
  // The registry already carries ლოჯია/лоджия as DB aliases; these are the inflected and
  // Latin forms an agent types. No bare 'loji' — three characters and too close to
  // ordinary text to claim a span safely.
  loggia:              ['ლოჯით', 'ლოჯიანი', 'logia', 'loggia'],
  // B1021 defects. Whole-word only, and every one of these is a noun no other facet owns.
  // ⚠️ 'ობი' is three characters — exactly the index floor — and is safe only because
  // matching is whole-word; it must never be allowed to match as a fragment, where it
  // sits inside ordinary Georgian words.
  damp_stain:          ['ნესტი', 'ნესტიანი', 'ტენიანი', 'nesti'],
  mould:               ['ობის', 'ობიანი', 'obi'],
  peeling_paint:       ['აქერცლილი', 'ჩამოცვენილი საღებავი', 'ჩამოცვენილი შპალერი'],
  cracked_surface:     ['ბზარი', 'ბზარები', 'ნაპრალი', 'bzari'],
  // B836. The bare noun the ads use; no Latin form has been observed in queries yet, so only
  // the attested Georgian goes in — the compiler's vocabulary grows from search_query_log,
  // never from guesses (DESK_SEARCH_COMPILER.md).
  occupancy_never_lived_in: ['უცხოვრებელი', 'უცხოვრებელ'],
  air_conditioner:     ['კონდიციონერით', 'kondicioneri', 'konditsioneri', 'air conditioning', 'air conditioner', 'кондиционер', 'კონდიციონერი', 'кондиционером', 'кондиционеры', 'air conditioned'],   // v21: + the English/Russian phrase
  storage_room:        ['სათავსოთი', 'satavso'],
  cellar:              ['სარდაფით', 'sardafi'],
  fireplace:           ['ბუხრით', 'buxari', 'bukhari', 'камин'],
  // v26: English "lift" (the word a visitor types; the registry only says "Elevator") and the Russian genitive/instrumental
  // that follow "без" / "с" ("без лифта", "с лифтом") — the compiler stems Georgian only, so Russian cases are listed
  elevator:            ['ლიფტით', 'ლიფტიანი', 'lifti', 'лифт', 'lift', 'лифта', 'лифтом'],
  parking:             ['პარკინგით', 'parkingi', 'парковка'],
  garage:              ['გარაჟით', 'garaji', 'avtofarexi'],
  garage_underground:  ['underground parking', 'подземный паркинг'],
  amen_concierge:      ['konsierji', 'консьерж'],
  sec_guard:           ['დაცვით', 'datsva', 'dacva', 'ohrana', 'security'],
  heat_central:        ['ცენტრალური გათბობით', 'centraluri gatboba', 'отопление'],
  utility_hot_water:   ['ცხელი წყლით', 'cxeli wyali', 'tskheli tskali'],
  utility_gas:         ['გაზით', 'gazi', 'газ'],
  utility_internet:    ['ინტერნეტით', 'interneti', 'интернет', 'wifi', 'wi-fi'],
  utility_ventilation: ['ვენტილაციით', 'ventilacia', 'ventilatsia'],
  washing_machine:     ['სარეცხი მანქანით', 'sarecxi manqana', 'стиральная машина', 'стиралка', 'სარეცხი მანქანა', 'стиральной машиной', 'стиральная машинка', 'стиральной машинкой', 'стиралкой', 'washer'],
  fridge:              ['მაცივრით', 'macivari', 'მაცივარი', 'холодильник', 'холодильником', 'refrigerator'],
  dishwasher:          ['ჭურჭლის სარეცხი მანქანა', 'посудомоечная', 'посудомойка', 'ჭურჭლის სარეცხი მანქანით', 'посудомоечной машиной', 'посудомоечная машина', 'посудомойкой'],
  tv:                  ['ტელევიზორი', 'ტელევიზორით', 'телевизор', 'телевизором', 'television'],
  builtin_kitchen:     ['ჩაშენებული სამზარეულოთი', 'chashenebuli samzareulo'],
}

// ── district aliases ──────────────────────────────────────────────────────────
// The district list itself comes from the DB (getDistricts) and is matched by
// stemmed name automatically — you do NOT need to list official names here.
// This map is for EXTRA names: latin spellings, common typos, colloquial names,
// neighborhoods people say instead of the official district. Key = district code.
//
// v2 (2026-07-06): rewritten for the district HIERARCHY (core migration 0053) —
// every ubani is its own code with parent_code → raion, and search understands
// parent/child, so an alias should point at the MOST SPECIFIC code, not the raion.
// TBS_DIGOMI was DELETED in 0053 (merged into TBS_DIDI_DIGOMI) — bare "დიღომი" now
// maps there (real-estate usage; the D6 decision in core/data/gemini-districts/REVIEW.md).
//
// ⚠️ COLLISION RULE: the engine indexes each space/hyphen-split part of an alias
// (≥3 chars) separately. NEVER put a generic word in an alias — 'ზღვის უბანი' would
// index 'უბანი' alone and hijack every query containing it. Same reason 'პლატო',
// 'მასივი', 'ცენტრი' are absent: each names 2+ places (see
// core/data/gemini-districts/ambiguity.json) — they stay residual rather than
// compile to a confident wrong chip.
// ── v23: numbered sub-areas (engine v30) ─────────────────────────────────────
// "გლდანის მე-3 მ/რ", "Глдани 3 мкр", "gldani 3 mikro" = Gldani 3rd Microdistrict. The type word is REQUIRED — a number
// without its type never resolves (geography skill, DIGHOMI.md): "gldani 3" stays Gldani + the text "3".
export const AREA_TYPE_WORDS = {
  micro:  ['მიკრორაიონი', 'მიკრორაიონის', 'მიკრორაიონში', 'მ/რ', 'მ.რ.', 'მ.რ', 'მრ', 'mikroraioni', 'mikro', 'mikr', 'mkr', 'm/r',
           'microdistrict', 'micro district', 'micro-district', 'micro', 'микрорайон', 'микрорайоне', 'мкр', 'мкр.', 'м/р', 'мр'],
  block:  ['კვარტალი', 'კვარტალის', 'კვარტალში', 'კვ.', 'კვ', 'kvartali', 'kvartal', 'kv', 'block', 'quarter', 'квартал', 'квартале', 'кв.', 'кв'],
  massif: ['მასივი', 'მასივის', 'მასივში', 'masivi', 'massif', 'massive', 'массив', 'массиве'],
}

export const DISTRICT_ALIASES = {
  // ── v22: the historic city name. The site languages' own names are NOT typed here: district_name_i18n holds all of
  // them (366 × 8, owner-reviewed) and reaches the compiler through the gazetteer (core 2523, engine v29).
  MUN_BATUMI: ['batum', 'батум'],
  // ── raions (official 10; DB names auto-match, these are latin/colloquial extras).
  // v3 additions come from the Gemini typo/slang pass (core/data/gemini-districts/
  // typos.json) — only NON-derivable forms live here; systematic shift/phonetic typos
  // (ორტაწალა, ჩუგურეთი, თემკა…) are handled generatively by the engine-v3 folds. ──
  TBS_VAKE:        ['vake', 'wake', 'ვაკე', 'ваке'],
  TBS_SABURTALO:   ['saburtalo', 'საბურთალო', 'saburthalo', 'saburtalio', 'sabu', 'საბუ', 'საბ', 'сабу', 'сабуртало', 'ვაჟა', 'vazha', 'важа',
                    // v6: 'ფშაველა' (Vazha-Pshavela ave/zone) — proven-demand residual, 4 hits in
                    // search_query_log compiling to nothing ("ვაჟა ფშ" left ფშ residual). ვაჟა already
                    // maps here; ფშაველა alone did not. Corroborated by the metro anchor (ვაჟა-ფშაველა → საბურთალო).
                    'ფშაველა', 'pshavela', 'fshavela'],
  TBS_DIDUBE:      ['didube', 'დიდუბე'],
  TBS_GLDANI:      ['gldani', 'გლდანი', 'глдани'],
  TBS_NADZALADEVI: ['nadzaladevi', 'ნაძალადევი', 'ნახალოვკა', 'nakhalovka', 'naxalovka', 'nazaladevi'],
  TBS_ISANI:       ['isani', 'ისანი'],
  TBS_SAMGORI:     ['samgori', 'სამგორი'],
  TBS_CHUGURETI:   ['chugureti', 'chughureti', 'cugureti', 'chughurethi', 'მარჯანიშვილი', 'marjanishvili', 'марджанишвили', 'маржанишвили'],
  TBS_MTATSMINDA:  ['mtatsminda', 'mtacminda'],
  TBS_KRTSANISI:   ['krtsanisi', 'krcanisi'],
  // ── cross-raion zone (kept parentless in 0053 — ex-raion, abolished 2013) ──
  TBS_OLD:         ['old tbilisi', 'ძველი თბილისი', 'oldtbilisi', 'dzveli tbilisi', 'dzveli tbilisshi', 'старый тбилиси', 'старом тбилиси', 'старого тбилиси', 'старый город', 'старом городе'],   // v21: 'dzveli' alone resolved to Dzveli Vedzisi
  // ── ubnebi (0053 hierarchy; alias → most specific code, raion comes via parent) ──
  TBS_VERA:            ['vera', 'ვერა'],
  TBS_SOLOLAKI:        ['sololaki', 'სოლოლაკი'],
  TBS_ABANOTUBANI:     ['abanotubani'],
  TBS_ORTACHALA:       ['ortachala', 'ortatchala', 'ortawala'],
  TBS_AVLABARI:        ['avlabari', 'alvabari', 'მეტეხი', 'რიყე'],
  TBS_VAZISUBANI:      ['vazisubani'],
  TBS_NAVTLUGHI:       ['navtlughi', 'navtlugi'],
  TBS_MERVE_LEGIONI:   ['ლეგიონი', 'legioni'],
  TBS_VARKETILI:       ['varketili', 'warketili', 'varkethili', 'варкетили'],
  TBS_ORKHEVI:         ['orkhevi'],
  TBS_LILO:            ['lilo', 'ლილო'],
  TBS_AFRIKA:          ['africa', 'afrika'],
  TBS_DIDI_DIGOMI:     ['digomi', 'დიღომი', 'დიღმის', 'dighomi', 'дигоми', 'დიდი დიღმის', 'didi dighmis', 'didi digmis'],   // v23: the genitive ("დიდი დიღმის II მ/რ")
  TBS_VASHLIJVARI:     ['vashlijvari', 'vaslijvari'],
  TBS_NUTSUBIDZE_PLATO:['ნუცუბიძე', 'nutsubidze', 'nutsu', 'нуцубидзе', 'nutsubidze plato', 'nutsubidzis plato', 'нуцубидзе плато'],   // v23: "plato" as people write it
  TBS_VEDZISI:         ['vedzisi', 'ვეძისი'],
  TBS_DELISI:          ['delisi', 'დელისი'],
  TBS_LISI:            ['lisi', 'ლისი'],
  TBS_BAGEBI:          ['bagebi', 'ბაგები'],
  TBS_TSKNETI:         ['tskneti', 'წყნეთი'],
  TBS_ZGHVISUBANI:     ['თემქა', 'temka', 'temqa', 'zgvisubani', 'themka'],
  TBS_SANZONA:         ['sanzona', 'სანზონა'],
  TBS_GHRMAGHELE:      ['ghrmaghele', 'ღრმაღელე'],
  TBS_LOTKINI:         ['lotkini', 'ლოტკინი'],
  TBS_MUKHIANI:        ['mukhiani', 'მუხიანი', 'muxiani', 'muhiani'],
  TBS_AVCHALA:         ['avchala', 'ავჭალა'],
  TBS_ZAHESI:          ['zahesi', 'ზაჰესი'],
  TBS_PLEKHANOVI:      ['plekhanovi', 'plexanovi', 'პლეხანოვი'],
  TBS_VORONTSOVI:      ['vorontsovi', 'ვორონცოვი'],
  TBS_KUKIA:           ['kukia', 'კუკია'],
  TBS_KOJORI:          ['kojori', 'კოჯორი'],
  TBS_KIKETI:          ['kiketi', 'კიკეთი'],
  TBS_TSAVKISI:        ['tsavkisi', 'წავკისი'],
  TBS_TABAKHMELA:      ['tabakhmela', 'ტაბახმელა'],
  TBS_OKROKANA:        ['okrokana', 'ოქროყანა'],
  TBS_SHINDISI:        ['shindisi', 'შინდისი'],
  TBS_CHOLOKASHVILI:   ['cholokashvili', 'ჩოლოყაშვილი'],
  // TBS_PATARA_DIGOMI deliberately has NO alias entry: any latin alias would split
  // into a 'digomi' part and, being later in this map, overwrite TBS_DIDI_DIGOMI's
  // bare-'დიღომი' mapping (last write wins in the stem index). Its DB name still
  // auto-matches the full "პატარა დიღომი" phrase via the stemmer.
}

// ── v29 (2026-10-22, typed-search audit): district NEGATION ─────────────────────────────────────────
// "არა ვაკე" / "not vake" / "except vake" / "кроме ваке" / "გარდა ვაკისა" / "ვაკის გარდა" all searched IN Vake.
// A district right after a BEFORE word (or right before an AFTER word) is the district ✕ (districtCodesNot).
export const DISTRICT_NEGATION_BEFORE = ['არა', 'არ', 'not', 'not in', 'except', 'except in', 'excluding', 'but not', 'no',
  'кроме', 'не', 'не в', 'без', 'исключая', 'გარდა', 'ara']
export const DISTRICT_NEGATION_AFTER = ['გარდა', 'არა', 'არ მინდა']
// ── v29: a COUNT minimum — "მინიმუმ 3 ოთახი" = 3 rooms or more (the 3, 4 and 5+ boxes) ──────────────
export const COUNT_MIN_WORDS = ['მინიმუმ', 'არანაკლებ', 'at least', 'min', 'minimum', 'минимум', 'не менее', 'от']
// ── v29: a floor ABOVE / BELOW a number — "3 სართულზე მაღლა" = floor 4 or higher ───────────────────
export const FLOOR_ABOVE_WORDS = ['მაღლა', 'ზემოთ', 'ზევით', 'მაღალი', 'above', 'higher', 'выше']
export const FLOOR_BELOW_WORDS = ['დაბლა', 'ქვემოთ', 'ქვევით', 'დაბალი', 'below', 'lower', 'ниже']
// ── v29: a price PER m² — "1500$/მ²", "1500$ per m2", "1000$ კვ.მ" — is not a total price ──────────────
// The Desk has no price-per-m² filter, so these amounts are left unread rather than becoming priceMax 1500.
export const PRICE_PER_WORDS = ['/', 'per', 'a', 'за', 'ერთი']
